import React, { useState, useEffect } from 'react';
import axios from '../../Api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel, faChevronLeft, faChevronRight, faSearch } from '@fortawesome/free-solid-svg-icons';
import SearchableSelect from '../../components/SearchableSelect';

const CompletedControls = () => {
  const [controls, setControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [surveys, setSurveys] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalControls, setTotalControls] = useState(0);
  const [statistics, setStatistics] = useState({
    total: 0,
    validated: 0,
    rejected: 0,
    hold: 0
  });
  const controlsPerPage = 10;
  const emptyFilters = {
    controller_id: '',
    interviewer_id: '',
    action: '',
    survey_id: '',
    date_from: '',
    date_to: '',
    source: ''
  };
  const [filters, setFilters] = useState(emptyFilters);
  const [pendingFilters, setPendingFilters] = useState(emptyFilters);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchControls();
  }, [filters, currentPage]);

  useEffect(() => {
    // Fetch surveys only once on component mount
    fetchSurveys();
  }, []);

  const fetchSurveys = async () => {
    try {
      // Every survey that has at least one check, resolved server-side across all three
      // paths a check reaches a survey through. Deriving this from a page of checks used to
      // drop surveys off the filter a few weeks after their last control.
      const response = await axios.get('/control/checks/surveys');
      setSurveys(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching surveys:', error);
    }
  };

  const fetchControls = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        per_page: controlsPerPage
      });
      if (filters.controller_id) params.append('controller_id', filters.controller_id);
      if (filters.interviewer_id) params.append('interviewer_id', filters.interviewer_id);
      if (filters.action) params.append('action', filters.action);
      if (filters.survey_id) params.append('survey_id', filters.survey_id);
      if (filters.date_from) params.append('from_date', filters.date_from);
      if (filters.date_to) params.append('to_date', filters.date_to);
      if (filters.source) params.append('source', filters.source);
      if (searchTerm && searchTerm.trim()) params.append('search', searchTerm.trim());

      const response = await axios.get(`/control/checks?${params.toString()}`);
      // Handle paginated response from Laravel
      if (response.data.data) {
        setControls(response.data.data);
        setTotalPages(response.data.last_page || 1);
        setTotalControls(response.data.total || 0);
        // Set statistics if available
        if (response.data.statistics) {
          setStatistics(response.data.statistics);
        }
      } else if (Array.isArray(response.data)) {
        setControls(response.data);
        setTotalPages(1);
        setTotalControls(response.data.length);
        // Calculate statistics from array if no statistics provided
        setStatistics({
          total: response.data.length,
          validated: response.data.filter(c => c.action === 'validate').length,
          rejected: response.data.filter(c => c.action === 'reject').length,
          hold: response.data.filter(c => c.action === 'hold').length
        });
      } else {
        setControls(response.data.data || []);
        setTotalPages(response.data.last_page || 1);
        setTotalControls(response.data.total || 0);
        // Set statistics if available
        if (response.data.statistics) {
          setStatistics(response.data.statistics);
        }
      }

    } catch (error) {
      console.error('Error fetching controls:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionBadge = (action) => {
    const badges = {
      validate: 'bg-green-100 text-green-800',
      reject: 'bg-red-100 text-red-800',
      hold: 'bg-yellow-100 text-yellow-800'
    };
    const labels = {
      validate: 'Validé',
      reject: 'Rejeté',
      hold: 'En attente'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${badges[action] || 'bg-gray-100 text-gray-800'}`}>
        {labels[action] || action}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.controller_id) params.append('controller_id', filters.controller_id);
      if (filters.interviewer_id) params.append('interviewer_id', filters.interviewer_id);
      if (filters.action) params.append('action', filters.action);
      if (filters.survey_id) params.append('survey_id', filters.survey_id);
      if (filters.date_from) params.append('from_date', filters.date_from);
      if (filters.date_to) params.append('to_date', filters.date_to);
      if (filters.source) params.append('source', filters.source);
      if (searchTerm && searchTerm.trim()) params.append('search', searchTerm.trim());

      const response = await axios.get(`/control/checks/export?${params.toString()}`, {
        responseType: 'blob',
        timeout: 300000, // 5 min — exports of large datasets can legitimately take a while
      });

      // Create a blob from the response
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'controles_completes.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting controls:', error);
      alert('Erreur lors de l\'exportation. Veuillez réessayer.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-xl text-gray-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Contrôles Complétés</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">
              Liste de tous les questionnaires contrôlés
            </p>
          </div>
          <button
            onClick={handleExport}
            className="inline-flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium w-full sm:w-auto"
          >
            <FontAwesomeIcon icon={faFileExcel} className="mr-2" />
            Exporter en Excel
          </button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="text-sm text-gray-500">Total Contrôles</div>
            <div className="text-2xl font-bold text-gray-800 mt-1">{statistics.total}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="text-sm text-gray-500">Validés</div>
            <div className="text-2xl font-bold text-green-600 mt-1">
              {statistics.validated}
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="text-sm text-gray-500">Rejetés</div>
            <div className="text-2xl font-bold text-red-600 mt-1">
              {statistics.rejected}
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="text-sm text-gray-500">En Attente</div>
            <div className="text-2xl font-bold text-yellow-600 mt-1">
              {statistics.hold}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
          {/* Search Bar */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Recherche</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Rechercher par enquête, enquêteur, contrôleur, ID soumission, commentaire, téléphone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      setCurrentPage(1);
                      fetchControls();
                    }
                  }}
                  className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                />
                <FontAwesomeIcon 
                  icon={faSearch} 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" 
                />
              </div>
              <button
                onClick={() => {
                  setCurrentPage(1);
                  fetchControls();
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
              >
                Rechercher
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-8 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
              <select
                value={pendingFilters.action}
                onChange={(e) => setPendingFilters({ ...pendingFilters, action: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
              >
                <option value="">Toutes</option>
                <option value="validate">Validé</option>
                <option value="reject">Rejeté</option>
                <option value="hold">En attente</option>
              </select>
            </div>
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Enquête</label>
              <SearchableSelect
                options={surveys}
                selectedValue={pendingFilters.survey_id ? parseInt(pendingFilters.survey_id) : null}
                onChange={(value) => setPendingFilters({ ...pendingFilters, survey_id: value ? String(value) : '' })}
                placeholder="-- Toutes les enquêtes --"
                searchPlaceholder="Rechercher par titre ou ID..."
                getOptionLabel={(s) => s.title}
                getOptionMeta={(s) => s.limesurvey_id ? `ID: ${s.limesurvey_id}` : null}
                maxHeight="200px"
                triggerClassName="px-3 py-2"
                textClassName="text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date début</label>
              <input
                type="date"
                value={pendingFilters.date_from}
                onChange={(e) => setPendingFilters({ ...pendingFilters, date_from: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date fin</label>
              <input
                type="date"
                value={pendingFilters.date_to}
                onChange={(e) => setPendingFilters({ ...pendingFilters, date_to: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
              <select
                value={pendingFilters.source}
                onChange={(e) => setPendingFilters({ ...pendingFilters, source: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
              >
                <option value="">Toutes</option>
                <option value="human">Humain</option>
                <option value="ai">IA</option>
              </select>
            </div>
            <div className="sm:col-span-2 lg:col-span-2 flex items-end gap-2">
              <button
                onClick={() => {
                  setFilters(pendingFilters);
                  setCurrentPage(1);
                }}
                className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
              >
                Appliquer
              </button>
              <button
                onClick={() => {
                  setPendingFilters(emptyFilters);
                  setFilters(emptyFilters);
                  setSearchTerm('');
                  setCurrentPage(1);
                }}
                className="w-full sm:w-auto px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
              >
                Réinitialiser
              </button>
            </div>
          </div>
        </div>

        {/* Controls Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Enquête
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Enquêteur
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contrôleur
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Note
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Correction
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Commentaire
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {controls.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                      Aucun contrôle trouvé
                    </td>
                  </tr>
                ) : (
                  controls.map((control) => (
                    <tr key={control.id} className="hover:bg-gray-50">
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(control.checked_at)}
                      </td>
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {control.assignment?.survey?.title || control.submission?.survey?.title || control.f2f_submission?.survey?.title || 'N/A'}
                        </div>
                        <div className="text-xs text-gray-500">
                          ID: {control.assignment?.survey?.limesurvey_id || control.submission?.survey?.limesurvey_id || control.f2f_submission?.survey?.limesurvey_id || 'N/A'}
                        </div>
                        {(control.assignment?.submission?.response_id || control.assignment?.f2f_submission?.response_id || control.submission?.response_id || control.f2f_submission?.response_id) && (
                          <div className="text-xs text-gray-500">
                            Soumission #{control.assignment?.submission?.response_id || control.assignment?.f2f_submission?.response_id || control.submission?.response_id || control.f2f_submission?.response_id}
                          </div>
                        )}
                      </td>
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                        {control.assignment?.supervisor_id ? (
                          <>
                            <div className="text-sm font-medium text-gray-900">
                              {control.assignment?.supervisor?.name || 'N/A'}
                              <span className="ml-1 px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">Prestataire</span>
                            </div>
                            <div className="text-xs text-gray-500">
                              Ref: {control.assignment?.supervisor?.ref || 'N/A'}
                            </div>
                          </>
                        ) : control.f2f_submission?.person ? (
                          <>
                            <div className="text-sm font-medium text-gray-900">
                              {control.f2f_submission.person.name || 'N/A'}
                              <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">F2F</span>
                            </div>
                            <div className="text-xs text-gray-500">
                              Ref: {control.f2f_submission.person.ref || 'N/A'}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="text-sm font-medium text-gray-900">
                              {control.assignment?.interviewer?.name || control.submission?.interviewer?.name || 'N/A'}
                              {(control.assignment?.is_f2f || control.is_f2f) && (
                                <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">F2F</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500">
                              Ref: {control.assignment?.interviewer?.ref || control.submission?.interviewer?.ref || 'N/A'}
                            </div>
                            {control.assignment?.is_f2f && control.assignment?.interviewer?.supervisor && (
                              <div className="text-xs text-purple-600">
                                Prestataire: {control.assignment.interviewer.supervisor.name}
                              </div>
                            )}
                          </>
                        )}
                      </td>
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {control.is_ai ? (
                          <span className="px-2 py-0.5 text-xs font-semibold bg-indigo-100 text-indigo-700 rounded">IA</span>
                        ) : (
                          <>
                            {control.controller?.name || control.controlled_by_user?.name || 'N/A'}
                            {control.controlled_by_user && !control.controller && (
                              <span className="ml-1 px-1.5 py-0.5 text-xs bg-indigo-100 text-indigo-700 rounded">Resp. Contrôle</span>
                            )}
                          </>
                        )}
                      </td>
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-yellow-400 text-lg mr-1">★</span>
                          <span className="text-sm font-medium text-gray-900">{control.rating}/5</span>
                        </div>
                      </td>
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                        {getActionBadge(control.action)}
                      </td>
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {control.correction_applied ? (
                          <span className="text-red-600 font-medium">Oui</span>
                        ) : (
                          <span className="text-gray-400">Non</span>
                        )}
                      </td>
                      <td className="px-4 lg:px-6 py-4 text-sm text-gray-500 min-w-[200px] max-w-[300px]">
                        <div className="max-h-20 overflow-y-auto break-words">
                          {control.comment || '-'}
                          {control.action === 'reject' && control.reject_reason && (
                            <div className="text-xs text-red-600 mt-1">
                              Motif: {control.reject_reason}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="block md:hidden">
            {controls.length === 0 ? (
              <div className="px-4 py-12 text-center text-gray-500">
                Aucun contrôle trouvé
              </div>
            ) : (
              controls.map((control) => (
                <div key={control.id} className="border-b border-gray-200 p-4 last:border-b-0">
                  <div className="space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-gray-900">
                          {formatDate(control.checked_at)}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-yellow-400 text-lg">★</span>
                          <span className="text-sm font-medium text-gray-900">{control.rating}/5</span>
                          {getActionBadge(control.action)}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-gray-600 font-medium">Enquête:</span>
                        <div className="text-gray-900">{control.assignment?.survey?.title || control.submission?.survey?.title || control.f2f_submission?.survey?.title || 'N/A'}</div>
                        <div className="text-xs text-gray-500">ID: {control.assignment?.survey?.limesurvey_id || control.submission?.survey?.limesurvey_id || control.f2f_submission?.survey?.limesurvey_id || 'N/A'}</div>
                        {(control.assignment?.submission?.response_id || control.assignment?.f2f_submission?.response_id || control.submission?.response_id || control.f2f_submission?.response_id) && (
                          <div className="text-xs text-gray-500">
                            Soumission #{control.assignment?.submission?.response_id || control.assignment?.f2f_submission?.response_id || control.submission?.response_id || control.f2f_submission?.response_id}
                          </div>
                        )}
                      </div>

                      <div>
                        <span className="text-gray-600 font-medium">
                          {(control.assignment?.supervisor_id || control.f2f_submission?.person_type === 'supervisor') ? 'Prestataire:' : 'Enquêteur:'}
                        </span>
                        {control.assignment?.supervisor_id ? (
                          <>
                            <div className="text-gray-900">
                              {control.assignment?.supervisor?.name || 'N/A'}
                              <span className="ml-1 px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">Prestataire</span>
                            </div>
                            <div className="text-xs text-gray-500">Ref: {control.assignment?.supervisor?.ref || 'N/A'}</div>
                          </>
                        ) : control.f2f_submission?.person ? (
                          <>
                            <div className="text-gray-900">
                              {control.f2f_submission.person.name || 'N/A'}
                              <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">F2F</span>
                            </div>
                            <div className="text-xs text-gray-500">Ref: {control.f2f_submission.person.ref || 'N/A'}</div>
                          </>
                        ) : (
                          <>
                            <div className="text-gray-900">
                              {control.assignment?.interviewer?.name || control.submission?.interviewer?.name || 'N/A'}
                              {(control.assignment?.is_f2f || control.is_f2f) && (
                                <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">F2F</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500">Ref: {control.assignment?.interviewer?.ref || control.submission?.interviewer?.ref || 'N/A'}</div>
                            {control.assignment?.is_f2f && control.assignment?.interviewer?.supervisor && (
                              <div className="text-xs text-purple-600">
                                Prestataire: {control.assignment.interviewer.supervisor.name}
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      <div>
                        <span className="text-gray-600 font-medium">Contrôleur:</span>
                        <div className="text-gray-900">
                          {control.is_ai ? (
                            <span className="px-2 py-0.5 text-xs font-semibold bg-indigo-100 text-indigo-700 rounded">IA</span>
                          ) : (
                            <>
                              {control.controller?.name || control.controlled_by_user?.name || 'N/A'}
                              {control.controlled_by_user && !control.controller && (
                                <span className="ml-1 px-1.5 py-0.5 text-xs bg-indigo-100 text-indigo-700 rounded">Resp. Contrôle</span>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      <div>
                        <span className="text-gray-600 font-medium">Correction:</span>
                        <div className={control.correction_applied ? 'text-red-600 font-medium' : 'text-gray-400'}>
                          {control.correction_applied ? 'Oui' : 'Non'}
                        </div>
                      </div>

                      {control.comment && (
                        <div>
                          <span className="text-gray-600 font-medium">Commentaire:</span>
                          <div className="text-gray-900 text-xs mt-1">{control.comment}</div>
                        </div>
                      )}

                      {control.action === 'reject' && control.reject_reason && (
                        <div>
                          <span className="text-gray-600 font-medium">Motif de rejet:</span>
                          <div className="text-red-600 text-xs mt-1">{control.reject_reason}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4">
            <div className="text-xs sm:text-sm text-gray-700 text-center sm:text-left whitespace-nowrap">
              <span>
                Affichage de {((currentPage - 1) * controlsPerPage) + 1} à {Math.min(currentPage * controlsPerPage, totalControls)} sur {totalControls} contrôles
              </span>
            </div>
            
            <div className="flex items-center space-x-1 sm:space-x-2 flex-nowrap justify-center min-w-0">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-2 sm:px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
              >
                <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
              </button>
              
              <div className="flex space-x-1 flex-nowrap">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors flex-shrink-0 ${
                        currentPage === pageNum
                          ? 'bg-red-600 text-white'
                          : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-2 sm:px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
              >
                <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompletedControls;

